import { Alert, Button, LoadingState, useToast } from '@orbind/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'
import { useSession } from '../../lib/session.tsx'
import './plugin-frame.css'

/**
 * Hospeda a UI de um plugin (SPEC-20260926-2036-plugin-iframe-e-ponte).
 *
 * O que isola código de terceiro aqui é a ORIGEM, não o atributo `sandbox`. O
 * iframe aponta para a origem do plugin, e o navegador já o separa da nossa.
 * O `sandbox` fecha o resto — principalmente `allow-top-navigation`, que é o
 * que impediria um plugin de trocar a página do Orbind por outra coisa.
 *
 * `allow-same-origin` está ali de propósito e não é contradição: ele dá ao
 * iframe a origem DELE (o plugin precisa dos próprios cookies), não a nossa.
 * Retirá-lo daria ao plugin uma origem opaca, e ele deixaria de conseguir
 * falar com o próprio backend.
 *
 * A URL é assinada pelo servidor e vale 5 minutos. Ela entra no histórico do
 * navegador — por isso a validade curta, e por isso o `qsh`: mesmo recuperada,
 * não serve para chamar outra coisa no plugin.
 */

export type PluginFrameProps = {
  tipo: 'rail-screen' | 'work-item-panel'
  chave: string
  /** Quando o plugin está num painel de Item. */
  workItemId?: string
  /** Altura inicial; a ponte ajusta depois. */
  alturaInicial?: number
  /** O host cresce sozinho (painel) ou ocupa tudo (tela)? */
  encaixe?: 'conteudo' | 'tela'
  onFechar?: () => void
  onRecarregarItem?: () => void
}

type Frame = {
  url: string
  origem: string
  pluginKey: string
  installationId: string
  workspaceId: string
  expiraEm: number
}

const MARCA = 'orbind-plugin'
/** Plugin que não carrega neste tempo provavelmente não vai carregar. */
const LIMITE_MS = 15_000

export function PluginFrame({
  tipo,
  chave,
  workItemId,
  alturaInicial = 240,
  encaixe = 'conteudo',
  onFechar,
  onRecarregarItem,
}: PluginFrameProps) {
  const session = useSession()
  const navigate = useNavigate()
  const toast = useToast()
  const ref = useRef<HTMLIFrameElement>(null)

  const [altura, setAltura] = useState(alturaInicial)
  const [carregou, setCarregou] = useState(false)
  const [demorou, setDemorou] = useState(false)

  const consulta = useQuery({
    queryKey: ['plugins', 'frame', tipo, chave, workItemId ?? null],
    // a URL assinada expira; renovar antes disso evita o iframe recarregar
    // num token morto quando a pessoa deixa a aba aberta
    staleTime: 4 * 60 * 1000,
    queryFn: async () =>
      (await api.plugins.extensions[':tipo'][':chave'].frame.$get({
        param: { tipo, chave },
        query: workItemId ? { workItemId } : {},
      })).json() as Promise<Frame>,
  })

  const frame = consulta.data ?? null

  /* ------------------------------------------------------------- a ponte -- */

  const responderContexto = useCallback(() => {
    const janela = ref.current?.contentWindow
    if (!janela || !frame) return
    janela.postMessage(
      {
        marca: MARCA,
        tipo: 'contexto',
        contexto: {
          installationId: frame.installationId,
          pluginKey: frame.pluginKey,
          workspaceId: frame.workspaceId,
          membro: { id: session.id, nome: session.name },
          locale: 'pt-BR',
          tema: document.documentElement.dataset['theme'] === 'dark' ? 'escuro' : 'claro',
        },
      },
      // NUNCA '*': a origem do plugin é a que veio do servidor, e mandar o
      // contexto para qualquer origem entregaria dado da pessoa a quem
      // estivesse no iframe se a URL tivesse sido trocada
      frame.origem,
    )
  }, [frame, session])

  useEffect(() => {
    if (!frame) return

    function aoReceber(ev: MessageEvent) {
      // três conferências: a origem é a registrada, veio do NOSSO iframe, e
      // tem a marca. Mensagem que falhe em qualquer uma morre em silêncio —
      // responder a origem desconhecida é o buraco clássico de postMessage.
      if (ev.origin !== frame!.origem) return
      if (ev.source !== ref.current?.contentWindow) return
      const dado = ev.data as { marca?: string; tipo?: string; [k: string]: unknown } | null
      if (!dado || dado.marca !== MARCA) return

      switch (dado.tipo) {
        case 'pronto':
          responderContexto()
          break
        case 'altura': {
          const px = Number(dado['px'])
          // teto para o plugin não empurrar a página para fora da tela
          if (Number.isFinite(px)) setAltura(Math.min(Math.max(px, 80), 4000))
          break
        }
        case 'aviso':
          toast.push({
            tone: (dado['tone'] as 'info') ?? 'info',
            message: String(dado['texto'] ?? '').slice(0, 300),
          })
          break
        case 'navegar': {
          const rota = String(dado['rota'] ?? '')
          // só rota interna: um plugin não redireciona a pessoa para fora
          if (rota.startsWith('/') && !rota.startsWith('//')) navigate(rota)
          break
        }
        case 'fechar':
          onFechar?.()
          break
        case 'recarregar-item':
          onRecarregarItem?.()
          break
      }
    }

    window.addEventListener('message', aoReceber)
    return () => window.removeEventListener('message', aoReceber)
  }, [frame, responderContexto, navigate, toast, onFechar, onRecarregarItem])

  /* ------------------------------------------------------------- estados -- */

  useEffect(() => {
    if (carregou || !frame) return
    const t = setTimeout(() => setDemorou(true), LIMITE_MS)
    return () => clearTimeout(t)
  }, [carregou, frame])

  if (consulta.isPending) {
    return <LoadingState variant="detail" label="Abrindo o plugin" />
  }

  if (consulta.isError || !frame) {
    return (
      <Alert
        tone="danger"
        title="Não foi possível abrir o plugin"
        action={
          <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Button>
        }
      >
        {consulta.error instanceof Error
          ? consulta.error.message
          : 'A extensão pode ter sido desinstalada.'}
      </Alert>
    )
  }

  return (
    <div className={`pfr pfr--${encaixe}`}>
      {!carregou && !demorou ? (
        <div className="pfr__espera">
          <LoadingState rows={3} label={`Carregando ${frame.pluginKey}`} />
        </div>
      ) : null}

      {demorou && !carregou ? (
        <div className="pfr__espera">
          {/* plugin fora do ar degrada só esta região — o resto da tela fica de pé */}
          <Alert tone="warning" title="O plugin está demorando">
            <>
              <code>{frame.origem}</code> não respondeu em 15 segundos. O serviço do plugin pode
              estar fora do ar.
            </>
          </Alert>
        </div>
      ) : null}

      <iframe
        ref={ref}
        className="pfr__frame"
        src={frame.url}
        title={`Plugin ${frame.pluginKey}`}
        style={encaixe === 'conteudo' ? { height: altura } : undefined}
        onLoad={() => setCarregou(true)}
        // allow-same-origin dá ao plugin a origem DELE, não a nossa.
        // Sem allow-top-navigation: o plugin não troca a página do Orbind.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    </div>
  )
}
